import { useCallback, useEffect, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import { fetchWithAuth } from "@/lib/utils";
import i18n from "@/lib/i18n";

export type McpCredentialScope = "user" | "organization";
export type McpAgentKind = "personal" | "shared";

export interface McpCredentialState {
  configured: boolean;
}

export interface McpServerCredentials {
  user: McpCredentialState;
  organization: McpCredentialState;
}

export interface McpServerAgent {
  id: string;
  display_name: string;
  kind: McpAgentKind;
  assigned: boolean;
  credential_scope: McpCredentialScope;
  credential_configured: boolean;
}

export interface McpServer {
  id: string;
  display_name: string;
  description: string;
  credential_label: string;
  credential_help: string;
  credentials: McpServerCredentials;
  agents: McpServerAgent[];
}

export interface McpServersData {
  servers: McpServer[];
}

export interface UseMcpServersReturn {
  data: McpServersData | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setAssignment: (serverId: string, agentId: string, assigned: boolean) => Promise<boolean>;
  saveCredential: (
    serverId: string,
    scope: McpCredentialScope,
    value: string,
  ) => Promise<boolean>;
  deleteCredential: (serverId: string, scope: McpCredentialScope) => Promise<boolean>;
}

async function responseError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body === "object") {
      const message = body.detail ?? body.error ?? body.message;
      if (typeof message === "string") return message;
    }
  } catch {
    // Fall back to the HTTP status when the response does not contain JSON.
  }
  return `HTTP ${response.status}: ${response.statusText}`;
}

export function useMcpServers(getToken: () => Promise<string>): UseMcpServersReturn {
  const [data, setData] = useState<McpServersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth("/api/mcp/servers", { headers: {} }, getToken);
      if (!response.ok) throw new Error(await responseError(response));
      setData((await response.json()) as McpServersData);
    } catch (err: any) {
      setError(err?.message ?? i18n.t("connections:mcpServers.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const mutate = useCallback(
    async (path: string, method: "PUT" | "DELETE", body?: object): Promise<boolean> => {
      setSaving(true);
      setError(null);
      try {
        const response = await fetchWithAuth(
          path,
          {
            method,
            headers: body ? { "Content-Type": "application/json" } : {},
            ...(body ? { body: JSON.stringify(body) } : {}),
          },
          getToken,
        );
        if (!response.ok) throw new Error(await responseError(response));
        setData((await response.json()) as McpServersData);
        return true;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("connections:mcpServers.errors.save"));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getToken],
  );

  const setAssignment = useCallback(
    (serverId: string, agentId: string, assigned: boolean) =>
      mutate(`/api/mcp/servers/${encodeURIComponent(serverId)}/assignments`, "PUT", {
        agent_id: agentId,
        assigned,
      }),
    [mutate],
  );

  const saveCredential = useCallback(
    (serverId: string, scope: McpCredentialScope, value: string) =>
      mutate(`/api/mcp/servers/${encodeURIComponent(serverId)}/credentials`, "PUT", {
        scope,
        value,
      }),
    [mutate],
  );

  const deleteCredential = useCallback(
    (serverId: string, scope: McpCredentialScope) => {
      const query = new URLSearchParams({ scope });
      return mutate(
        `/api/mcp/servers/${encodeURIComponent(serverId)}/credentials?${query.toString()}`,
        "DELETE",
      );
    },
    [mutate],
  );

  useRefreshOnReconnect(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    data,
    loading,
    saving,
    error,
    refresh,
    setAssignment,
    saveCredential,
    deleteCredential,
  };
}
